/** Small per-tick state machines of the input layer: hold-or-toggle, hold-to-confirm, throw range. Pure. */

import { GRENADE } from '../sim/tuning';

/** Run, crawl and sneak (02 §13, ROUND7 §2): a toggle by default, as the original, or held - the player's choice (08 §4). */
export class Latch {
  private on = false;

  /** `held`: the control is down now. `pressed`: it went down since the last update. */
  update(held: boolean, pressed: boolean, toggle: boolean): boolean {
    if (!toggle) this.on = held;
    else if (pressed) this.on = !this.on;
    return this.on;
  }

  get value(): boolean {
    return this.on;
  }

  reset(): void {
    this.on = false;
  }
}

/** Recall (02 §13): true for exactly one tick once the control has been held for `ticks` ticks. */
export class HoldTrigger {
  private held = 0;

  constructor(private readonly ticks: number) {}

  update(down: boolean): boolean {
    if (!down) {
      this.held = 0;
      return false;
    }
    this.held++;
    return this.held === this.ticks;
  }
}

/** Where the crosshair rests, and how far it walks while `use` is held, metres (02 §7: 5-22 m). */
export const THROW_REST = 14;
export const THROW_MIN = 5;
/**
 * The far end of the walk is the simulation's own grenade range, not a hand-kept 22 that happened
 * to match it. R6: `heavy.ts` throws `min(his reach, cmd.aimDist)`, so an input that could never
 * report more than the BASE range paid Long Arm's "+25 % grenade throw range" exactly nothing
 * standing up - the perk was dead on the one scheme, and on the pad it could not even reach 22.
 */
export const THROW_MAX = GRENADE.maxRange;
/** `reach` is the thrower's own `SimMods.grenadeRange`: the crosshair goes as far as his arm does. */
export const throwMax = (reach: number): number => THROW_MAX * reach;
/** Ticks for the crosshair to walk from `THROW_MIN` to `THROW_MAX`. */
export const THROW_WALK_TICKS = 60;

/**
 * Classic scheme: there is no mouse to say how far a grenade goes, so holding `use` walks the
 * crosshair outward (02 §13). The tick `use` goes down still reports the rest distance - the sim
 * may throw on the press - and the walked distance is held through the tick of the release, for
 * a sim that throws then.
 */
export class ThrowRange {
  private held = 0;
  private last = THROW_REST;

  update(use: boolean, reach = 1): number {
    if (!use) {
      const released = this.held > 1 ? this.last : THROW_REST;
      this.held = 0;
      this.last = THROW_REST;
      return released;
    }
    this.held++;
    if (this.held === 1) return THROW_REST;
    const t = Math.min(1, (this.held - 2) / THROW_WALK_TICKS);
    const far = throwMax(reach);
    this.last = Math.round((THROW_MIN + (far - THROW_MIN) * t) * 4) / 4;
    return this.last;
  }
}
